/**
 * Save Button Management
 * Handles the save button state and save functionality
 */
import { beginLoadingCursor, endLoadingCursor } from './loading-cursor';
import { isTourKeyEventAllowed } from './tour-spotlight';
class SaveButton {
    constructor() {
        this.isSaving = false;
        this.saveEnabled = true;
        // Global keyboard shortcut
        $(document).on('keydown', (e) => {
            // Cmd+S (Mac) or Ctrl+S (Windows/Linux)
            // Cmd+Shift+S is reserved for font Save As
            if ((e.metaKey || e.ctrlKey) && e.key === 's' && !e.shiftKey) {
                if (!isTourKeyEventAllowed(e)) {
                    e.preventDefault();
                    return;
                }
                e.preventDefault();
                void this.handleSave();
            }
        });
        // Register UI callbacks with Python backend
        this.registerCallbacks();
    }
    /**
     * Register callback functions that Python can call via js module
     */
    registerCallbacks() {
        // Create JavaScript functions that Python can call via js._fontSaveCallbacks
        window._fontSaveCallbacks = {
            beforeSave: (fontId, filename) => {
                console.log('[SaveButton]', `🔵 beforeSave callback: ${filename}`);
            },
            afterSave: (fontId, filename, duration) => {
                const callbackStart = performance.now();
                const fname = filename.split('/').pop();
                console.log('[SaveButton]', `🟢 afterSave callback: ${fname} (Python reported: ${duration.toFixed(2)}s)`);
                // Update dirty indicator (font was just saved, so it's clean)
                if (window.fontManager) {
                    window.fontManager.updateDirtyIndicator();
                }
                // Update save button state
                this.isSaving = false;
                this.showSuccess();
                const callbackDuration = performance.now() - callbackStart;
                console.log('[SaveButton]', `⏱️ afterSave callback completed in ${callbackDuration.toFixed(0)}ms`);
            },
            onError: (fontId, filename, error) => {
                console.error('[SaveButton]', `❌ Save failed: ${error}`);
                this.isSaving = false;
                this.showError();
            }
        };
        console.log('[SaveButton]', 'Save callbacks registered (Python will call via js module)');
    }
    /**
     * Handle save action
     */
    async handleSave() {
        if (!this.saveEnabled) {
            return;
        }
        // Unsaved font with disk plugin but no file handle → redirect to Save As
        const currentFont = window.fontManager?.currentFont;
        if (currentFont &&
            !currentFont.fileHandle &&
            !currentFont.isCloudBacked()) {
            if (!this.hasFontOpen())
                return;
            await window.showFontFileDialog?.({ mode: 'save-as' });
            return;
        }
        if (!this.canSave()) {
            return;
        }
        const saveStartTime = performance.now();
        console.log('[SaveButton]', '🔵 Save button clicked');
        this.isSaving = true;
        beginLoadingCursor();
        try {
            // Get current font
            const currentFont = window.fontManager?.currentFont;
            if (!currentFont) {
                throw new Error('No font loaded');
            }
            const pluginId = currentFont.sourcePlugin.getId();
            console.log('[SaveButton]', `${currentFont.sourcePlugin.getIcon()} Saving to ${pluginId}...`);
            const saveOperationStart = performance.now();
            // Sync JSON from object model first
            currentFont.syncJsonFromModel();
            // Save via plugin
            await currentFont.save();
            // Update dirty indicator
            if (window.fontManager) {
                await window.fontManager.updateDirtyIndicator();
            }
            const duration = (performance.now() - saveOperationStart) / 1000;
            console.log('[SaveButton]', `✅ Save completed in ${duration.toFixed(2)}s`);
            // Trigger callbacks
            if (window._fontSaveCallbacks) {
                window._fontSaveCallbacks.afterSave?.(window.fontManager.currentFontId, currentFont.path, duration);
            }
            // Success - callbacks have already handled UI updates
            // Note: isSaving is set to false by afterSave callback
            const totalDuration = performance.now() - saveStartTime;
            console.log('[SaveButton]', `⏱️ Total save duration: ${totalDuration.toFixed(0)}ms`);
        }
        catch (error) {
            console.error('[SaveButton]', 'Error saving font:', error);
            // Only show error if callbacks haven't already handled it
            // (callbacks might have been called even if we got an error here)
            if (this.isSaving) {
                this.isSaving = false;
                this.showError();
            }
        }
        finally {
            endLoadingCursor();
        }
    }
    /**
     * Update button state based on current font
     */
    updateButtonState() {
        window.dispatchEvent(new CustomEvent('fontSaveStateChanged'));
    }
    hasFontOpen() {
        return !!window.fontManager?.currentFont;
    }
    isDirty() {
        return !!window.fontManager?.hasUnsyncedChanges?.(window.fontManager?.currentFont);
    }
    canSave() {
        return (this.saveEnabled &&
            !this.isSaving &&
            this.hasFontOpen() &&
            this.isDirty());
    }
    canSaveAs() {
        return this.saveEnabled && !this.isSaving && this.hasFontOpen();
    }
    /**
     * Show success feedback
     */
    showSuccess() {
        this.updateButtonState();
        setTimeout(() => {
            if (!this.isSaving) {
                this.updateButtonState();
            }
        }, 1500);
    }
    /**
     * Show error feedback
     */
    showError() {
        this.updateButtonState();
        setTimeout(() => {
            if (!this.isSaving) {
                this.updateButtonState();
            }
        }, 2000);
    }
}
// Initialize save button when DOM is ready
$(document).ready(() => {
    window.saveButton = new SaveButton();
});
